/**
 * Mandar un texto por el canal que corresponda.
 *
 * ── El problema que resuelve ────────────────────────────────────────────────
 * La bandeja dejó de ser solo WhatsApp (ADR 0022/0023/0024): ahora hay
 * conversaciones del chat del sitio y preguntas de Mercado Libre. Pero el
 * compositor seguía mandando TODO a `whatsapp-send-message`, que desde el ADR
 * 0023 rechaza con 409 cualquier otro canal.
 *
 * O sea: el agente veía la conversación, escribía la respuesta, y le rebotaba.
 * Este módulo es lo único que separaba «el equipo lo ve» de «el equipo lo
 * contesta».
 *
 * ── Por qué acá y no en el backend ─────────────────────────────────────────
 * Cada canal responde por un sistema distinto —WhatsApp por su proveedor,
 * Mercado Libre por su API, el widget dejando el mensaje para que lo levante—.
 * No hay un «enviar» común que valga la pena inventar: lo que hay es una
 * decisión, y vive en un solo lugar.
 *
 * 🪤 SOLO ENRUTA TEXTO. Reacciones, audios, ubicaciones y plantillas son
 * capacidades de WhatsApp; un visitante web no las tiene. Esas siguen yendo
 * derecho a `whatsapp-send-message`, y su guarda de canal las corta si la
 * conversación no es de WhatsApp — que es el comportamiento correcto, no una
 * omisión.
 */

import { supabase } from '@/lib/supabase';

export type ClaseDeContacto = 'phone' | 'email' | 'web' | 'external';

export interface EnvioDeTexto {
  conversationId: string;
  /** La clase del contacto. Si no se conoce, se resuelve consultando. */
  clase?: ClaseDeContacto | string | null;
  texto: string;
  /** Solo aplica a WhatsApp. */
  esNotaInterna?: boolean;
  claveIdempotencia?: string;
  citandoMensajeId?: string | null;
  mencionados?: string[];
}

export interface ResultadoEnvio {
  ok: boolean;
  /** Texto para el agente cuando no salió. Ya viene en castellano. */
  error?: string;
}

/**
 * La clase del contacto de una conversación.
 *
 * Se consulta si quien llama no la trajo. Es una lectura chica e indexada, y
 * evita tener que cambiar la firma de cinco componentes para pasar un dato que
 * la conversación ya tiene.
 */
async function claseDeLaConversacion(conversationId: string): Promise<string> {
  const { data } = await supabase
    .from('wa_conversations')
    .select('contact_kind')
    .eq('id', conversationId)
    .maybeSingle();
  // Sin dato = WhatsApp. Es el caso de todas las conversaciones anteriores al
  // ADR 0022, que son la enorme mayoría.
  return ((data as { contact_kind?: string | null } | null)?.contact_kind) ?? 'phone';
}

export async function enviarTexto(envio: EnvioDeTexto): Promise<ResultadoEnvio> {
  const clase = envio.clase ?? await claseDeLaConversacion(envio.conversationId);

  // ── Chat del sitio web ───────────────────────────────────────────────────
  // La respuesta queda en la bandeja y el widget la levanta en su próxima
  // consulta. Que eso cuente como entrega depende de que el widget consulte:
  // ver `/poll` en chatbot-api.
  if (clase === 'web') {
    if (envio.esNotaInterna) return await notaInterna(envio);
    const { data, error } = await supabase.functions.invoke('chatbot-agent-reply', {
      body: { conversation_id: envio.conversationId, body: envio.texto },
    });
    return resultado(data, error);
  }

  // ── Mercado Libre ────────────────────────────────────────────────────────
  // Responder publica en la publicación: primero sale allá, después se registra
  // acá. Si ML lo rechaza, nada queda escrito y el agente ve por qué.
  if (clase === 'external') {
    if (envio.esNotaInterna) return await notaInterna(envio);
    const { data, error } = await supabase.functions.invoke('meli-answer-question', {
      body: { conversation_id: envio.conversationId, text: envio.texto },
    });
    return resultado(data, error);
  }

  // ── WhatsApp (y todo lo anterior al multicanal) ──────────────────────────
  const { data, error } = await supabase.functions.invoke('whatsapp-send-message', {
    body: {
      conversation_id: envio.conversationId,
      body: envio.texto,
      msg_type: 'text',
      ...(envio.claveIdempotencia ? { client_idempotency_key: envio.claveIdempotencia } : {}),
      ...(envio.esNotaInterna ? { is_internal_note: true } : {}),
      ...(envio.citandoMensajeId ? { quoted_message_id: envio.citandoMensajeId } : {}),
      ...(envio.mencionados?.length ? { mentioned_ids: envio.mencionados } : {}),
    },
  });
  return resultado(data, error);
}

/**
 * Una nota interna no sale a ningún lado: es del equipo.
 *
 * 🪤 POR ESO NO PASA POR EL CANAL. Mandar una nota interna a la API de Mercado
 * Libre la publicaría EN LA PUBLICACIÓN, a la vista de cualquiera. Se escribe
 * directo en la bandeja, para cualquier canal.
 */
async function notaInterna(envio: EnvioDeTexto): Promise<ResultadoEnvio> {
  const { error } = await supabase.from('wa_messages').insert({
    conversation_id: envio.conversationId,
    direction: 'outbound',
    sender_type: 'agent',
    body: envio.texto,
    msg_type: 'text',
    is_internal_note: true,
    delivery_status: 'delivered',
  });
  return error ? { ok: false, error: error.message } : { ok: true };
}

function resultado(data: unknown, error: unknown): ResultadoEnvio {
  const delServidor = (data as { error?: string } | null)?.error;
  if (delServidor) return { ok: false, error: delServidor };
  if (error) {
    const msg = (error as { message?: string }).message;
    return { ok: false, error: msg ?? 'No se pudo enviar el mensaje.' };
  }
  return { ok: true };
}
